import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as TimeIcon } from '@pkg/assets/icons/v2/icn-time.svg';
import SecurityCodeInput, { SecurityCodeInputVariants } from '@pkg/components/Input/SecurityCodeInput';
import { DepButton } from '@pkg/components/deprecated';
import { useAppDispatch } from '@pkg/hooks';
import useFormattedTime from '@pkg/hooks/useFormattedTime';
import { TwoFactorAuthType } from '@pkg/types/enums';

import { Auth } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import config from '@app/config';
import { login2FA } from '@app/store/actions/auth';
import { addAlert, set2faResetCode } from '@app/store/slices';
import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

import { AuthenticatorType } from '../../../types';

const RESEND_TIMEOUT = 120;
const { twoFACodeDigitCount } = config;

interface AuthSetupEnterCodeProps {
    onNext: () => void;
    authType: AuthenticatorType;
    phone: string;
}

export const AuthSetupEnterCode = ({ onNext, authType, phone }: AuthSetupEnterCodeProps) => {
    const { t } = useTranslation(['twoFactorAuth']);
    const [authCodes, setAuthCodes] = useState('');
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const tokens = useAuthTokens((rootState) => rootState.token);
    const [remainingSeconds, setRemainingSeconds] = useState(RESEND_TIMEOUT);

    const formattedTime = useFormattedTime(remainingSeconds);

    const detailText = useMemo(() => {
        if (authType === AuthenticatorType.AuthenticatorApp) {
            return t('2fa_step_setup_authenticator_enter_detail_authenticator');
        }

        return t('2fa_step_setup_authenticator_enter_detail_phone', { last4: phone.slice(-4) });
    }, [authType, phone, t]);

    const onConfirmCode = (e) => {
        e.preventDefault();
        setLoading(true);
        apiFactory()
            .call(Auth.confirm2FA, { value: { code: authCodes } })
            .then((res) => {
                if (!res.ok) {
                    throw res.error;
                }
                if (tokens) {
                    dispatch(login2FA(authCodes, tokens.refresh_token));
                    onNext();
                }
            })
            .catch(() => {
                dispatch(addAlert({ message: t('2fa_invalid_code') }));
                dispatch(set2faResetCode(true));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onResend = () => {
        apiFactory()
            .call(Auth.enable2FA, { value: { provider: TwoFactorAuthType.SMS, phone, default: true } })
            .then((result) => {
                if (!result.ok) {
                    dispatch(addAlert({ message: result.error as string }));
                    return;
                }
                setRemainingSeconds(RESEND_TIMEOUT);
                dispatch(set2faResetCode(true));
            });
    };

    useEffect(() => {
        const timerId = setInterval(() => {
            setRemainingSeconds((r) => (r > 0 ? r - 1 : r));
        }, 1000);

        return () => {
            clearInterval(timerId);
        };
    }, []);

    return (
        <>
            <h2 className="two-factor-auth-step__title mb-small">{t('2fa_step_setup_authenticator_enter_title')}</h2>
            <p className="two-factor-auth-step__detail mb-xsmall" dangerouslySetInnerHTML={{ __html: detailText }} />
            <form className="two-factor-auth-step__form" onSubmit={(e) => onConfirmCode(e)}>
                <div className="two-factor-auth-step__inputfield">
                    <SecurityCodeInput
                        setCompleteCode={(code) => setAuthCodes(code)}
                        digitCount={twoFACodeDigitCount}
                        variant={SecurityCodeInputVariants.MidSpaced}
                        digitOnly
                    />
                </div>
                <DepButton
                    loading={loading}
                    disabled={authCodes.length < twoFACodeDigitCount}
                    classes="two-factor-auth-step__action"
                    type="submit"
                >
                    Next
                </DepButton>
            </form>
            {authType === AuthenticatorType.PhoneNumber &&
                (remainingSeconds === 0 ? (
                    <p className="two-factor-auth-step__altaction action" onClick={onResend}>
                        <span>{t('2fa_step_setup_authenticator_enter_resend')}</span>
                    </p>
                ) : (
                    <p className="two-factor-auth-step__altaction">
                        <span>{t('2fa_step_setup_authenticator_enter_resend_in', { timeString: formattedTime })}</span>
                        <TimeIcon />
                    </p>
                ))}
        </>
    );
};
